import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Users, Building2, CalendarCheck, UserPlus, Download, ClipboardList, FileText, FileSpreadsheet } from "lucide-react";
import { getStats, exportCsvUrl, exportXlsxUrl } from "@/lib/api";
import { formatMonthLabel } from "@/lib/format";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const StatCard = ({ icon: Icon, label, value, testid, tone = "navy" }) => (
  <Card data-testid={testid} className="border-slate-200 shadow-sm hover:shadow-md transition-all">
    <CardContent className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</div>
          <div className="text-3xl font-bold text-slate-900 mt-2 tracking-tight">{value}</div>
        </div>
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${tone === "gold" ? "bg-amber-50 text-[#C59B27]" : "bg-blue-50 text-[#0B2545]"}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getStats()
      .then(setStats)
      .catch(() => toast.error("Gagal memuat statistik"))
      .finally(() => setLoading(false));
  }, []);

  const chartData = (stats?.monthly || []).map(m => ({ bulan: formatMonthLabel(m.month), jumlah: m.count }));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#C59B27] font-semibold">Dashboard Resmi</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1 tracking-tight">
            Rekapitulasi Tamu Kunjungan
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Pantau arus kunjungan ke Sekretariat DPRD Kabupaten Seruyan. Catat, cetak absensi, dan terbitkan surat pernyataan resmi hanya dalam beberapa klik.
          </p>
        </div>
        <div className="flex gap-2">
          <Button asChild className="btn-primary" data-testid="dashboard-add-visitor-button">
            <Link to="/admin/tamu/baru"><UserPlus className="w-4 h-4 mr-2" /> Tamu Baru</Link>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="dashboard-export-menu">
                <Download className="w-4 h-4 mr-2" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <a href={exportXlsxUrl()}><FileSpreadsheet className="w-4 h-4 mr-2" /> Excel (.xlsx)</a>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a href={exportCsvUrl()}><FileText className="w-4 h-4 mr-2" /> CSV</a>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard icon={ClipboardList} label="Total Kunjungan" value={loading ? "…" : stats?.total_kunjungan ?? 0} testid="stat-total-kunjungan-card" />
        <StatCard icon={Users} label="Total Tamu & Pendamping" value={loading ? "…" : stats?.total_tamu ?? 0} testid="stat-total-tamu-card" tone="gold" />
        <StatCard icon={Building2} label="Instansi Berbeda" value={loading ? "…" : stats?.total_instansi ?? 0} testid="stat-total-instansi-card" />
        <StatCard icon={CalendarCheck} label="Kunjungan Hari Ini" value={loading ? "…" : stats?.kunjungan_hari_ini ?? 0} testid="stat-today-card" tone="gold" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-900">Grafik Kunjungan per Bulan</CardTitle>
          </CardHeader>
          <CardContent className="h-80">
            {chartData.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-500 text-sm">
                <FileText className="w-10 h-10 mb-2 text-slate-300" />
                Belum ada data kunjungan.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="bulan" tick={{ fontSize: 11, fill: "#64748B" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748B" }} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                  <Bar dataKey="jumlah" fill="#0B2545" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg text-slate-900">Instansi Terbanyak</CardTitle>
          </CardHeader>
          <CardContent>
            {(!stats?.top_instansi || stats.top_instansi.length === 0) ? (
              <div className="text-sm text-slate-500 py-6 text-center">Belum ada data.</div>
            ) : (
              <ul className="space-y-3">
                {stats.top_instansi.map((it, idx) => (
                  <li key={idx} className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2 last:border-0">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="w-7 h-7 rounded-full bg-[#0B2545] text-white text-xs font-bold flex items-center justify-center shrink-0">{idx + 1}</span>
                      <span className="text-sm text-slate-800 truncate">{it.instansi}</span>
                    </div>
                    <span className="text-sm font-semibold text-[#C59B27]">{it.count}×</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
